import React, { useState, useCallback } from 'react';
import { Button, IconButton, Snackbar, Alert } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import { ShareMenu } from './ShareMenu';
import { shareViaWebAPI, isWebShareSupported, isFileShareSupported } from './utils/shareUtils';
import { copyToClipboard } from './utils/clipboardUtils';
import { openSocialShare } from './utils/socialShareLinks';

/**
 * Generic ShareButton Component
 * 
 * @param {Object} props
 * @param {string} props.title - Share title
 * @param {string} props.text - Share text/description
 * @param {string} props.url - URL to share
 * @param {File|Blob|string} props.file - File/blob/dataURL to share
 * @param {string} props.fileName - Filename for file share
 * @param {Array<string>} props.platforms - Platforms to show in menu (default: all)
 * @param {boolean} props.showCopyLink - Show copy link option (default: true)
 * @param {string} props.variant - Button variant: 'button' | 'icon' (default: 'button')
 * @param {Function} props.onShare - Callback when share succeeds
 * @param {Function} props.onError - Callback when share fails
 * @param {Function} props.onCopyLink - Custom copy link handler
 */
export const ShareButton = ({
    title,
    text,
    url,
    file,
    fileName = 'share.png',
    platforms,
    showCopyLink = true,
    variant = 'button',
    onShare,
    onError,
    onCopyLink,
    ...buttonProps
}) => {
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const closeSnackbar = useCallback(() => {
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);

    const handleShareClick = useCallback(async (event) => {
        // Prepare share data
        const shareData = {
            title: title || '',
            text: text || '',
            url: url || window.location.href,
        };

        // Add file if provided
        let fileToShare = null;
        if (file) {
            try {
                if (typeof file === 'string') {
                    // Data URL - convert to File
                    const { dataURLtoFile } = await import('./utils/fileUtils');
                    fileToShare = dataURLtoFile(file, fileName);
                } else if (file instanceof Blob) {
                    // Blob - convert to File
                    const { blobToFile } = await import('./utils/fileUtils');
                    fileToShare = blobToFile(file, fileName);
                } else if (file instanceof File) {
                    fileToShare = file;
                }

                if (fileToShare) {
                    shareData.files = [fileToShare];
                }
            } catch (error) {
                console.error('Error processing file for share:', error);
                // Continue without file if conversion fails
            }
        }

        // Try Web Share API first (if supported)
        if (isWebShareSupported()) {
            // Check if file sharing is supported when we have a file
            if (fileToShare && !isFileShareSupported()) {
                // File sharing not supported, fallback to menu
                setMenuAnchor(event.currentTarget);
                return;
            }

            try {
                const result = await shareViaWebAPI(shareData);
                if (result.success) {
                    showSnackbar('Shared successfully!', 'success');
                    if (onShare) onShare('native');
                    return;
                } else if (result.cancelled) {
                    // User cancelled, do nothing
                    return;
                }
            } catch (error) {
                // Web Share API failed, fallback to menu
                if (error.name !== 'AbortError') {
                    setMenuAnchor(event.currentTarget);
                }
                return;
            }
        }

        // Fallback to menu
        setMenuAnchor(event.currentTarget);
    }, [title, text, url, file, fileName, onShare, showSnackbar]);

    const handleMenuClose = useCallback(() => {
        setMenuAnchor(null);
    }, []);

    const handlePlatformClick = useCallback((platform) => {
        const shareData = {
            title: title || '',
            text: text || '',
            url: url || window.location.href,
        };

        openSocialShare(platform, shareData);
        showSnackbar(`Opening ${platform}...`, 'info');
        if (onShare) onShare(platform);
    }, [title, text, url, onShare, showSnackbar]);

    const handleCopyLink = useCallback(async () => {
        const linkToCopy = url || window.location.href;

        if (onCopyLink) {
            onCopyLink(linkToCopy);
        } else {
            const copied = await copyToClipboard(linkToCopy);
            if (copied) {
                showSnackbar('Link copied to clipboard!', 'success');
            } else {
                showSnackbar('Failed to copy. Please copy manually.', 'error');
            }
        }
    }, [url, onCopyLink, showSnackbar]);

    const shareData = {
        title: title || '',
        text: text || '',
        url: url || window.location.href,
    };

    const ButtonComponent = variant === 'icon' ? IconButton : Button;
    const buttonContent = variant === 'icon' ? <ShareIcon /> : (
        <>
            <ShareIcon sx={{ mr: 1 }} />
            Share
        </>
    );

    return (
        <>
            <ButtonComponent
                onClick={handleShareClick}
                {...buttonProps}
                sx={{
                    ...(buttonProps.sx || {}),
                }}
            >
                {buttonContent}
            </ButtonComponent>

            <ShareMenu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                shareData={shareData}
                onPlatformClick={handlePlatformClick}
                showCopyLink={showCopyLink}
                onCopyLink={handleCopyLink}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

